/**
 * One conversation row on web, shared by the Messages page's conversation
 * panel (app/messages/_layout.web.tsx) and the My Tabbio Inbox pop-out, which
 * passes no `onDelete`, so it shows no delete control. The participant and
 * avatar helpers moved here with it; the layout's user-search row still uses
 * `Avatar` and `getOtherParticipant`. Web only (it renders DOM elements), but
 * has no DOM access at import time.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConversationSummary } from "@tabbio/types";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import { getSystemConversationIdentity } from "../../lib/system-sender-identity";

const defaultAvatar: string = require("../../../assets/default-avatar.png");
const systemSenderAvatars: Record<"tabbio" | "founder", string> = {
  tabbio: require("../../../assets/branding/admin-sender-tabbio.png"),
  founder: require("../../../assets/branding/admin-sender-founder.png"),
};

// ─── Helpers ────────────────────────────────────────────────────

export function getOtherParticipant(
  conversation: ConversationSummary,
  currentUserId: string,
  // The other side is missing only when they deleted their account.
  deletedUserName: string,
) {
  if (conversation.type === "system") {
    const identity = getSystemConversationIdentity(conversation)!;
    return {
      name: identity.name,
      initials: identity.name.slice(0, 2).toUpperCase(),
      avatarUrl: identity.avatarUrl,
      identityKey: identity.key,
    };
  }
  const other = conversation.participants.find(
    (p) => p.userId !== currentUserId,
  );
  if (!other) {
    return {
      name: deletedUserName,
      initials: "?",
      avatarUrl: null,
      identityKey: null,
    };
  }
  const parts = other.name.split(" ");
  const initials =
    parts.length >= 2
      ? `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase()
      : other.name.slice(0, 2).toUpperCase();
  return {
    name: other.name,
    initials,
    avatarUrl: other.image ?? null,
    identityKey: null,
  };
}

export function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

export function getUnreadCount(
  conversation: ConversationSummary,
  currentUserId: string,
): number {
  const me = conversation.participants.find((p) => p.userId === currentUserId);
  return me?.unreadCount ?? 0;
}

// ─── Avatar ─────────────────────────────────────────────────────

export function Avatar({
  name,
  url,
  initials,
  identityKey,
}: {
  name: string;
  url: string | null;
  initials: string;
  identityKey?: "tabbio" | "founder" | null;
}) {
  return url || identityKey ? (
    <img
      src={
        identityKey ? systemSenderAvatars[identityKey] : url || defaultAvatar
      }
      alt={name}
      referrerPolicy="no-referrer"
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        objectFit: "cover",
        flexShrink: 0,
      }}
    />
  ) : (
    <div
      aria-hidden
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "rgba(0,0,0,0.06)",
        color: "var(--text-secondary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

// ─── Conversation row ───────────────────────────────────────────

export function ConversationRow({
  conversation,
  currentUserId,
  isActive,
  onSelect,
  onDelete,
}: {
  conversation: ConversationSummary;
  currentUserId: string;
  isActive: boolean;
  onSelect: () => void;
  /** Without it there is no delete control (the My Tabbio pop-out). */
  onDelete?: () => void;
}) {
  const { t } = useTranslation("common");
  const other = getOtherParticipant(
    conversation,
    currentUserId,
    t("messages.deletedUser"),
  );
  const unread = getUnreadCount(conversation, currentUserId);
  const lastMsg = conversation.lastMessage?.isDeleted
    ? t("messages.messageDeleted")
    : (conversation.lastMessage?.content ?? "");
  const [hover, setHover] = useState(false);
  const [deleteFocused, setDeleteFocused] = useState(false);
  // The delete button is absolutely positioned over the timestamp slot, so the
  // time has to step aside while it is showing; otherwise the trash icon
  // renders on top of "1w". Without `onDelete` there is nothing to step aside for.
  const showDelete = Boolean(onDelete) && (hover || deleteFocused);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        borderRadius: 16,
        cursor: "pointer",
        background: isActive
          ? "var(--selected-bg)"
          : hover
            ? "var(--hover)"
            : "transparent",
        transition: "background-color 150ms",
      }}
    >
      <Avatar
        name={other.name}
        url={other.avatarUrl}
        initials={other.initials}
        identityKey={other.identityKey}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            dir="auto"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              fontWeight: unread > 0 ? 500 : 400,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {other.name}
          </span>
          <span
            aria-hidden={showDelete}
            style={{
              fontSize: 12,
              color: "var(--text-tertiary)",
              flexShrink: 0,
              opacity: showDelete ? 0 : 1,
              transition: "opacity 150ms",
            }}
          >
            {formatTimeAgo(conversation.lastMessageAt)}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 2,
          }}
        >
          <span
            dir="auto"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 13,
              color: "var(--text-secondary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {lastMsg}
          </span>
          {unread > 0 ? (
            <span
              style={{
                flexShrink: 0,
                minWidth: 18,
                height: 18,
                borderRadius: 9999,
                padding: "0 6px",
                background: "var(--text-primary)",
                color: "var(--bg)",
                fontSize: 11,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {unread}
            </span>
          ) : null}
        </div>
      </div>
      {onDelete ? (
        <span
          role="button"
          tabIndex={0}
          aria-label={t("messages.delete")}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              onDelete();
            }
          }}
          onFocus={() => setDeleteFocused(true)}
          onBlur={() => setDeleteFocused(false)}
          style={{
            position: "absolute",
            // Centred on the name/time line: 12px row padding + half of that
            // 20px line, minus half the 28px button.
            insetInlineEnd: 6,
            top: 8,
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-tertiary)",
            background: "transparent",
            opacity: showDelete ? 1 : 0,
            pointerEvents: showDelete ? "auto" : "none",
            cursor: "pointer",
            transition: "opacity 150ms, background-color 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--hover-strong)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <MaterialSymbol name="delete" size={16} />
        </span>
      ) : null}
    </div>
  );
}
